import { useRef } from "react";
import React from "react";
import { Button, Modal, Tag, Pagination } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import { convertToArabicDate } from "../../utils/date";
import { IMAGE_URL } from "../../constants";
import Logo from "../../assets/images/LOGOS-03.png";
import LogoStationnnement from "../../assets/images/stationnement-logo.png";
import LogoTanger from "../../assets/images/tanger-logo.png";
import PermissionRender from "../PermissionRender";
import useBulkPrintPreview from "../../hooks/useBulkPrintPreview";

const BulkViolationPdf = ({ infractions, visible, onClose }) => {
  const pdfRef = useRef();
  const user = useSelector((state) => state.user.user);
  const { visibleItems, previewPage, setPreviewPage, isPrinting, printOptions } = useBulkPrintPreview(infractions || []);

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const pageStyle = `
    @page {
      size: A4;
      margin: 15mm;
    }
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 210mm;
        height: 297mm;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .printable-area {
        width: 100%;
        height: auto;
        margin: 0 auto !important;
        padding: 10mm !important;
        border: none !important;
        box-shadow: none !important;
        overflow: visible !important;
        font-size: 10pt !important;
        line-height: 1.4 !important;
        font-family: Arial, sans-serif !important;
      }
      .printable-area * {
        color: #000 !important;
      }
      .printable-area h2 {
        font-size: 18pt !important;
        margin-top: 8pt !important;
        margin-bottom: 8pt !important;
        font-weight: bold !important;
        text-decoration: underline !important;
      }
      .printable-area p {
        font-size: 14pt !important;
        margin: 4pt 0 !important;
        font-weight: 500 !important;
      }
      .printable-area h6 {
        font-size: 14pt !important;
        font-weight: 600 !important;
      }
      .printable-area strong {
        font-weight: bold !important;
      }
      .page-break {
        page-break-before: always !important;
        break-before: page !important;
      }
      .bulk-page {
        min-height: 250mm;
        width: 100%;
      }
      .pdf-header {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 70px;
        background: white;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #ddd;
        z-index: 1000;
      }
      .pdf-header img {
        height: 70px !important;
        width: auto !important;
      }
      .pdf-footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        height: 15px;
        background: white;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 5px 20px;
        border-top: 1px solid #ddd;
        font-size: 10pt !important;
        z-index: 1000;
      }
      .page-content {
        margin-top: 67px;
        margin-bottom: 40px !important;
      }
      .first-page.page-content {
        margin-top: 33px !important;
      }
      .images-page {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 40mm;
        margin-top: 20px !important;
        padding-top: 20px !important;
      }
      .image-row {
        display: flex;
        justify-content: center;
        gap: 15mm;
        width: 100%;
        page-break-inside: avoid;
      }
      .image-row:nth-child(2) {
        page-break-after: always;
      }
      .infraction-image {
        width: 280px !important;
        height: auto !important;
        border: 2px solid #ccc;
        border-radius: 8px;
      }
      .images-continuation-page {
        margin-top: 100px !important;
        padding-top: 30px !important;
      }
      .no-print {
        display: none !important;
      }
    }
  `;

  const printNow = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `Contraventions_${infractions?.[0]?.data?.matricule || "bulk"}_A4`,
    pageStyle: pageStyle,
    ...printOptions,
  });

  const handlePrint = () => {
    if (pdfRef.current && !isPrinting) printNow();
  };

  const renderArabicReport = (infraction, isFirst) => {
    const date = infraction.datetime_infraction?.split(" ")[0];
    const time = infraction.datetime_infraction?.split(" ")[1];

    return (
      <div
        className={`${isFirst ? "first-page" : "page-break"} page-content bulk-page`}
      >
        <div
          style={{
            marginBottom: "15px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "15px" }}>
            <h6
              style={{
                margin: "2px 0",
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              المملكة المغربية
            </h6>
            <h6
              style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
              }}
            >
              وزارة الداخلية
            </h6>
            <h6
              style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
              }}
            >
              ولاية جهة طنجة - تطوان - الحسيمة
            </h6>
            <h6
              style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
              }}
            >
              عمالة طنجة - أصيلة
            </h6>
            <h6
              style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
              }}
            >
              جماعة طنجة
            </h6>
          </div>
          <img
            src={LogoTanger}
            alt="Logo Tanger"
            style={{ height: "80px", width: "auto", marginTop: "10px" }}
          />
        </div>

        <h2
          style={{
            textAlign: "center",
            fontSize: "20px",
            fontWeight: "bold",
            textDecoration: "underline",
            marginTop: "15px",
            marginBottom: "15px",
          }}
        >
          محضر معاينة مخالفة
        </h2>

        <div
          style={{
            marginBottom: "10px",
            textAlign: "right",
            fontSize: "15px",
          }}
        >
          <p style={{ margin: "4px 0" }} className="text-center font-bold">
            <strong>رقم : {infraction?.reference} </strong>
          </p>
          <p style={{ margin: "4px 0" }} className="text-center font-bold">
            بتاريخ:
            <strong> {" " + convertToArabicDate(date) + " "}</strong>
            على الساعة <strong>{" " + time + " "}</strong>
          </p>
        </div>

        <div
          style={{
            textAlign: "right",
            fontSize: "14px",
            lineHeight: 1.5,
            marginBottom: "10px",
          }}
        >
          <p style={{ margin: "4px 0" }}>
            * بناء على الظهير الشريف رقم 1-15-85 الصادر في 20 رمضان 1436
            (7 يوليوز 2015) بتنفيذ القانون التنظيمي رقم 113-14 المتعلق
            بالجماعات.
          </p>
          <p style={{ margin: "4px 0" }}>
            * بناء على الظهير الشريف رقم 1-06-15 الصادر في 15 محرم 1427
            (14 فبراير 2006) بتنفيذ القانون رقم 54-05 المتعلق بالتدبير
            المفوض للمرافق العامة.
          </p>
          <p style={{ margin: "4px 0" }}>
            * بناء على المرسوم رقم 2-78-157 المؤرخ في 11 رجب 1400 (26 ماي
            1980) المتعلق بالشروط التي تنفذ بها تلقائيا التدابير الرامية
            إلى استتباب الأمن وضمان سلامة المرور والصحة والمحافظة على
            الصحة العمومية.
          </p>
          <p style={{ margin: "4px 0" }}>
            * وبناء على القرار التنظيمي 1357/2022 بشأن تنظيم استغلال أماكن
            وقوف المركبات المؤدى عنه في إطار التدبير المفوض.
          </p>
          <p style={{ margin: "4px 0" }}>
            * وبناء على عقود التدبير المفوض لخدمة الركن بمواقف السيارات
            بمدينة طنجة.
          </p>
        </div>

        <div
          style={{
            textAlign: "right",
            fontSize: "15px",
            lineHeight: 1.5,
            marginBottom: "10px",
          }}
        >
          <p style={{ margin: "8px 0" }}>
            نحن الموقعون أسفله.{" "}
            <strong>السيد (ة) {infraction?.user?.full_name_ar}</strong>،
            العون المحلف، المزاول مهامه بشركة صوماجيك باركينغ والمكلف
            بالمراقبة، المعين بهذه الصفة بمقتضى القرار عدد
            <strong> {infraction?.user?.number} </strong> {""}
            بتاريخ <strong> {infraction?.user?.assigned_date}</strong> ،
            عاينا خلال جولة تفقدية من أجل مراقبة وزجر المخالفات المرتكبة
            من طرف مستعملي مواقف السيارات المشمولة بعقد التدبير المفوض
            لخدمة الركن بمدينة طنجة.
          </p>
          <p style={{ margin: "8px 0" }}>
            أن السيارة من نوع <strong>{infraction?.brand}</strong> رقم
            اللوحة{" "}
            <strong dir="ltr" style={{ display: "inline-block" }}>
              {infraction?.matricule}
            </strong>
          </p>
          <p style={{ margin: "8px 0" }}>
            قد خالفت مقتضيات دفتر الشروط واتفاقيات التدبير المفوض لخدمة
            الركن بجماعة طنجة.
          </p>
        </div>

        <div
          style={{
            textAlign: "right",
            marginBottom: "15px",
            fontSize: "15px",
          }}
        >
          <p style={{ margin: "5px 0" }}>
            طبيعة المخالفة:{" "}
            <strong>{infraction?.type_infraction?.name_ar}</strong>
          </p>
          <p style={{ margin: "5px 0" }}>
            محل عنوان ارتكاب المخالفة:{" "}
            <strong>{infraction?.location_ar}</strong>
          </p>
          <p style={{ margin: "5px 0" }}>
            وذلك يوم: <strong>{convertToArabicDate(date)}</strong> في
            الساعة: <strong>{time}</strong>
          </p>
          <p style={{ margin: "5px 0" }}>
            مبلغ المخالفة: <strong>{infraction?.amount} درهما</strong> رقم
            الوصل: <span className="inline-block w-20">&nbsp;</span>
            بتاريخ:
          </p>
        </div>

        <div
          style={{
            textAlign: "left",
            marginTop: "20px",
            marginBottom: "20px",
            fontSize: "15px",
          }}
        >
          <div style={{ display: "inline-block", textAlign: "center" }}>
            <p style={{ margin: "5px 0" }}>إمضاء: العون المحلف</p>
            <div className="my-7">
              <p>التوقيع: ........................................</p>
            </div>
            <p style={{ margin: "5px 0" }}>
              طنجة في: <strong>{convertToArabicDate(date)}</strong>
            </p>
          </div>
        </div>

        <div
          style={{
            textAlign: "center",
            fontSize: "13px",
            color: "#333",
            marginTop: "20px",
          }}
        >
          <p>
            تنبيه: في حالة العود تضاعف المخالفة (تسجل وتحفظ هذه المخالفة
            في برنامج معلوماتي)
          </p>
        </div>
      </div>
    );
  };

  const renderScreenshotPage = (screenshot, reference) => {
    return (
      <div className="page-break page-content bulk-page" dir="ltr">
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
            marginTop: "10px",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "#722ed1",
            }}
          >
            Détails de l'Infraction #{reference} - Capture d'écran
          </h2>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "250mm",
            width: "100%",
          }}
        >
          {screenshot ? (
            <img
              src={screenshot}
              alt={`Screenshot - ${reference}`}
              style={{
                maxWidth: "100%",
                maxHeight: "230mm",
                objectFit: "contain",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          ) : (
            <div
              style={{
                textAlign: "center",
                fontSize: "16px",
                color: "#666",
                padding: "50px",
              }}
            >
              Chargement de la capture d'écran...
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPhotosSection = (infraction) => {
    return (
      <div className="page-break page-content bulk-page">
        <div style={{ textAlign: "center", marginBottom: "30px" }}>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#722ed1",
            }}
          >
            Evidence Photos - {infraction.reference}
          </h2>
        </div>

        <div className="images-page">
          {infraction?.photos ? (
            (() => {
              const photos = infraction.photos.split(",");
              const elements = [];

              for (let i = 0; i < photos.length; i += 2) {
                const photo1 = photos[i];
                const photo2 = photos[i + 1];

                if (i > 0) {
                  elements.push(
                    <div
                      key={`break-${infraction.id}-${i}`}
                      className="page-break images-continuation-page"
                    >
                      <div
                        style={{
                          textAlign: "center",
                          marginBottom: "30px",
                        }}
                      >
                        <h2
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#722ed1",
                          }}
                        >
                          Evidence Photos - {infraction.reference} (Suite)
                        </h2>
                      </div>
                    </div>
                  );
                }

                elements.push(
                  <div key={`row-${infraction.id}-${i}`} className="image-row">
                    <div style={{ textAlign: "center" }}>
                      <img
                        src={IMAGE_URL + photo1}
                        alt={`Evidence ${i + 1}`}
                        className="infraction-image"
                        style={{
                          width: "280px",
                          objectFit: "contain",
                          border: "2px solid #ccc",
                          borderRadius: "8px",
                        }}
                      />
                      <p
                        style={{
                          fontSize: "14px",
                          marginTop: "10px",
                          fontWeight: "bold",
                        }}
                      >
                        Photo {i + 1}
                      </p>
                    </div>

                    {photo2 && (
                      <div style={{ textAlign: "center" }}>
                        <img
                          src={IMAGE_URL + photo2}
                          alt={`Evidence ${i + 2}`}
                          className="infraction-image"
                          style={{
                            width: "280px",
                            objectFit: "contain",
                            border: "2px solid #ccc",
                            borderRadius: "8px",
                          }}
                        />
                        <p
                          style={{
                            fontSize: "14px",
                            marginTop: "10px",
                            fontWeight: "bold",
                          }}
                        >
                          Photo {i + 2}
                        </p>
                      </div>
                    )}
                  </div>
                );
              }

              return elements;
            })()
          ) : (
            <div
              style={{
                textAlign: "center",
                fontSize: "16px",
                color: "#666",
              }}
            >
              No photos available for this infraction
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!infractions || infractions.length === 0) return null;

  return (
    <Modal
      open={visible}
      onCancel={isPrinting ? undefined : onClose}
      closable={!isPrinting}
      maskClosable={!isPrinting}
      keyboard={!isPrinting}
      footer={null}
      width="700px"
      centered
      styles={{ body: { maxHeight: "80vh", overflow: "auto" } }}
    >
      <div className="mb-4 text-left no-print flex items-center gap-3">
        <PermissionRender
          requiredPermission={["infractions", "print_violation"]}
        >
          <Button
            onClick={handlePrint}
            loading={isPrinting}
            type="primary"
            icon={<DownloadOutlined />}
          >
            طباعة ({infractions.length} مخالفات)
          </Button>
        </PermissionRender>
        <Tag color="purple">{infractions.length} infractions</Tag>
      </div>
      <div className="mb-2 no-print text-sm text-gray-600">
        Aperçu du document {previewPage} sur {infractions.length}. Le bouton d’impression imprime toute la sélection.
      </div>
      <Pagination
        className="mb-4 no-print"
        current={previewPage}
        pageSize={1}
        total={infractions.length}
        showSizeChanger={false}
        disabled={isPrinting}
        onChange={setPreviewPage}
      />

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflow: "hidden",
          paddingBottom: "20px",
        }}
      >
        <div
          style={{
            transform: "scale(0.85)",
            transformOrigin: "top center",
          }}
        >
          <div
            ref={pdfRef}
            className="printable-area bg-white text-black"
            dir="rtl"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "14px",
              margin: "0",
              padding: "40px",
              border: "1px solid #ccc",
              backgroundColor: "white",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            {/* Header with Logos */}
            <div className="pdf-header">
              <img src={Logo} alt="App Logo" style={{ width: "10px" }} />
              <img
                src={LogoStationnnement}
                alt="App Logo"
                style={{ width: "10px" }}
              />
            </div>

            {/* Render each infraction: Arabic Report + Screenshot + Photos */}
            {visibleItems.map((item, index) => (
              <React.Fragment key={item.data.id}>
                {renderArabicReport(item.data, index === 0)}
                {renderScreenshotPage(item.screenshot, item.data.reference)}
                {renderPhotosSection(item.data)}
              </React.Fragment>
            ))}

            {/* Footer */}
            <div className="pdf-footer">
              <span>طُبع بواسطة: {user?.full_name || "المستخدم"}</span>
              <span>تاريخ الطباعة: {getCurrentDate()}</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BulkViolationPdf;
